import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { FolderOpen, Plus, Pencil, Trash2, FileText, Search, Filter } from 'lucide-react';
import { SortSelect, sortByField } from '../../components/SortSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BELGE_TUR_LABELS = {
  sozlesme: 'Sözleşme', hakkedis: 'Hakediş', is_programi: 'İş Programı',
  yer_teslim_tutanagi: 'Yer Teslim Tutanağı', genel_sartname: 'Genel Şartname',
  ozel_sartname: 'Özel Şartname', teknik_sartname: 'Teknik Şartname',
  kesin_hesap: 'Kesin Hesap', gecici_kabul_tutanagi: 'Geçici Kabul Tutanağı',
  kesin_kabul_tutanagi: 'Kesin Kabul Tutanağı', sure_uzatim_karari: 'Süre Uzatım Kararı',
  devir_sozlesmesi: 'Devir Sözleşmesi', teminat_mektubu: 'Teminat Mektubu',
  fatura: 'Fatura', tutanak: 'Tutanak', rapor: 'Rapor', yazisma: 'Yazışma',
  proje: 'Proje', metraj: 'Metraj', diger: 'Diğer',
};

const emptyForm = { ihale_id: '', belge_turu: 'diger', baslik: '', aciklama: '', icerik: '' };

export default function ISYBelgeler({ ihaleler }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterIhale, setFilterIhale] = useState('all');
  const [filterTur, setFilterTur] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [viewItem, setViewItem] = useState(null);
  const [sortKey, setSortKey] = useState('newest');

  const fetchBelgeler = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/isy/belgeler`;
      const params = [];
      if (filterIhale !== 'all') params.push(`ihale_id=${filterIhale}`);
      if (filterTur !== 'all') params.push(`belge_turu=${filterTur}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterIhale, filterTur]);

  useEffect(() => { fetchBelgeler(); }, [fetchBelgeler]);

  const openCreate = () => { setFormData({ ...emptyForm }); setEditMode(false); setSelectedId(null); setDialogOpen(true); };

  const openEdit = async (item) => {
    try {
      const res = await axios.get(`${API}/isy/belgeler/${item.id}`, { withCredentials: true });
      const full = res.data;
      setFormData({
        ihale_id: full.ihale_id || '', belge_turu: full.belge_turu || 'diger',
        baslik: full.baslik || '', aciklama: full.aciklama || '', icerik: full.icerik || ''
      });
    } catch {
      setFormData({
        ihale_id: item.ihale_id || '', belge_turu: item.belge_turu || 'diger',
        baslik: item.baslik || '', aciklama: item.aciklama || '', icerik: ''
      });
    }
    setEditMode(true); setSelectedId(item.id); setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.baslik.trim()) { toast.error('Başlık zorunludur'); return; }
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.ihale_id) delete payload.ihale_id;
      if (editMode) {
        await axios.put(`${API}/isy/belgeler/${selectedId}`, payload, { withCredentials: true });
        toast.success('Belge güncellendi');
      } else {
        await axios.post(`${API}/isy/belgeler`, payload, { withCredentials: true });
        toast.success('Belge oluşturuldu');
      }
      setDialogOpen(false); fetchBelgeler();
    } catch (e) { toast.error('Hata oluştu'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/belgeler/${deleteId}`, { withCredentials: true });
      toast.success('Belge silindi'); setDeleteId(null); fetchBelgeler();
    } catch (e) { toast.error('Silme hatası'); }
  };

  const getIhaleAdi = (id) => (ihaleler || []).find(i => i.id === id)?.is_adi || '-';
  const filtered = items.filter(i => !searchText || i.baslik?.toLowerCase().includes(searchText.toLowerCase()));
  const SORT_DEFS_BELGE = {
    newest: { field: 'created_at', dir: -1 }, oldest: { field: 'created_at', dir: 1 },
    baslik_az: { field: 'baslik', dir: 1 }, baslik_za: { field: 'baslik', dir: -1 },
    tur_az: { field: 'belge_turu', dir: 1 }
  };
  const sortedBelgeler = sortByField(filtered, sortKey, SORT_DEFS_BELGE);

  return (
    <div className="space-y-4" data-testid="isy-belgeler">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-indigo-400" />
          <h2 className="text-lg font-bold text-white">Belge Yönetimi</h2>
          <Badge className="bg-gray-800 text-gray-400 text-[10px]">{items.length} belge</Badge>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="belge-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Yeni Belge
        </Button>
      </div>

      {/* Filtreler */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Belge ara..."
            className="bg-gray-800 border-gray-700 text-white h-8 text-sm pl-8" data-testid="belge-search" />
        </div>
        <Select value={filterIhale} onValueChange={setFilterIhale}>
          <SelectTrigger className="h-8 w-44 bg-gray-800 border-gray-700 text-white text-xs"><SelectValue placeholder="İhale" /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all" className="text-gray-300">Tüm İhaleler</SelectItem>
            {(ihaleler || []).map(ih => <SelectItem key={ih.id} value={ih.id} className="text-gray-300 text-xs">{ih.is_adi}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTur} onValueChange={setFilterTur}>
          <SelectTrigger className="h-8 w-40 bg-gray-800 border-gray-700 text-white text-xs"><SelectValue placeholder="Tür" /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all" className="text-gray-300">Tüm Türler</SelectItem>
            {Object.entries(BELGE_TUR_LABELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-gray-300 text-xs">{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
          { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
          { value: 'baslik_az', label: 'Başlık (A-Z)' }, { value: 'baslik_za', label: 'Başlık (Z-A)' },
          { value: 'tur_az', label: 'Tür (A-Z)' }
        ]} />
      </div>

      {/* Belge Listesi */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
      ) : sortedBelgeler.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-12 text-center text-gray-500">
            {items.length === 0 ? <>Henüz belge yok. <button onClick={openCreate} className="text-blue-400 underline ml-1">İlk belgeyi ekle</button></> : 'Arama sonucu bulunamadı'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sortedBelgeler.map(item => (
            <Card key={item.id} className="bg-gray-900/60 border-gray-700/50 hover:border-gray-600/50 transition-colors" data-testid={`belge-item-${item.id}`}>
              <CardContent className="py-2.5 px-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{item.baslik}</p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-500">
                        <Badge className="bg-indigo-900/20 text-indigo-300 text-[9px] px-1 py-0">{BELGE_TUR_LABELS[item.belge_turu] || item.belge_turu}</Badge>
                        {item.ihale_id && <span className="truncate max-w-[200px]">{getIhaleAdi(item.ihale_id)}</span>}
                        <span>{item.created_at?.split('T')[0]}</span>
                      </div>
                      {item.aciklama && <p className="text-[10px] text-gray-500 mt-0.5 truncate">{item.aciklama}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-amber-400" onClick={() => openEdit(item)}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(item.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editMode ? 'Belgeyi Düzenle' : 'Yeni Belge'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-gray-400 text-xs">Başlık *</Label>
              <Input value={formData.baslik} onChange={e => setFormData({...formData, baslik: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="belge-form-baslik" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Belge Türü</Label>
                <Select value={formData.belge_turu} onValueChange={v => setFormData({...formData, belge_turu: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                    {Object.entries(BELGE_TUR_LABELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-gray-300 text-xs">{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">İlgili İhale</Label>
                <Select value={formData.ihale_id || 'none'} onValueChange={v => setFormData({...formData, ihale_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="none" className="text-gray-300">Genel</SelectItem>
                    {(ihaleler || []).map(ih => <SelectItem key={ih.id} value={ih.id} className="text-gray-300 text-xs">{ih.is_adi}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Açıklama</Label>
              <Input value={formData.aciklama} onChange={e => setFormData({...formData, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">İçerik / Notlar</Label>
              <Textarea value={formData.icerik} onChange={e => setFormData({...formData, icerik: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-sm min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="belge-form-save">
              {saving ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Belgeyi Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Bu belgeyi silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 border-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
